import { useEffect, useState } from "react";
import {
  Box, Typography, Paper, Stack, Button, Alert, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress,
} from "@mui/material";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { uomsApi, type UomDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

export function UomsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<UomDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<UomDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("catalog.uom.create"));
  const canUpdate = useAuth(s => s.hasPermission("catalog.uom.edit"));
  const canDelete = useAuth(s => s.hasPermission("catalog.uom.delete"));

  const query = useQuery({
    queryKey: ["uoms", search, pagination.page, pagination.pageSize],
    queryFn: () => uomsApi.list({
      search: search || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "UoM deleted" },
    mutationFn: (id: string) => uomsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["uoms"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (u: UomDto) => { setEditing(u); setFormOpen(true); setMenuAnchor(null); };
  const closeMenu = () => { setMenuAnchor(null); setMenuRow(null); };
  const handleDelete = (u: UomDto) => {
    closeMenu();
    if (confirm(`Delete UoM "${u.code} — ${u.name}"? Blocked if any item references it.`)) {
      deleteMutation.mutate(u.id);
    }
  };

  const columns: GridColDef<UomDto>[] = [
    { field: "code", headerName: "Code", width: 140 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    {
      field: "actions", headerName: "", width: 60, sortable: false, filterable: false,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(p.row); }}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Units of Measure</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New UoM
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <TextField
          placeholder="Search by code or name"
          fullWidth size="small" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {canUpdate && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={() => menuRow && handleDelete(menuRow)}>
            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <UomDialog open={formOpen} onClose={() => setFormOpen(false)} uom={editing} />
    </Box>
  );
}

function UomDialog({ open, onClose, uom }: { open: boolean; onClose: () => void; uom: UomDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!uom;
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) { setCode(uom?.code ?? ""); setName(uom?.name ?? ""); }
  }, [open, uom]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "UoM updated" : "UoM created" },
    mutationFn: async () => {
      if (isEdit && uom) {
        await uomsApi.update(uom.id, { name });
        return uom.id;
      }
      const r = await uomsApi.create({ code, name });
      return r.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["uoms"] });
      qc.invalidateQueries({ queryKey: ["uoms-picker"] });
      onClose();
    },
  });

  const err = !code.trim() ? "Code required"
    : !name.trim() ? "Name required"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <DialogTitle>{isEdit ? "Edit UoM" : "New UoM"}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
          <Stack spacing={2}>
            <TextField label="Code *" size="small" fullWidth autoFocus
              value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={isEdit} helperText={isEdit ? "Code cannot be changed" : "e.g. EA, BOX, ML"} />
            <TextField label="Name *" size="small" fullWidth
              value={name} onChange={(e) => setName(e.target.value)} />
            {err && <Alert severity="info">{err}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
